import { useEffect, useRef, useState } from "react";
import { BsChevronDown } from "react-icons/bs";
import { useRoomStore } from "../store/roomStore";
import { adultsList } from "../data";

/**
 * Adults dropdown component for selecting number of adult guests.
 * 
 * Features:
 * - Custom dropdown with smooth animations
 * - Click outside to close
 * - Keyboard accessible (aria attributes)
 * - Selected state highlighting
 * - Chevron icon rotation on open/close
 * 
 * @returns {JSX.Element} Adults dropdown component
 * 
 * @remarks
 * - Uses Zustand store for state management instead of Context
 * - Closes dropdown when clicking outside via useRef and event listener
 * - Options are sourced from adultsList data
 */
export default function AdultsDropdown(): JSX.Element {
  const { adults, setAdults } = useRoomStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (value: string) => {
    setAdults(value);
    setIsOpen(false);
  };

  return (
    <div
      ref={dropdownRef}
      className="relative w-full h-full bg-white hover:bg-gray-50 transition-colors duration-200"
    >
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full h-full flex items-center justify-between px-6 outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-md"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Select number of adults"
      >
        <span className="font-secondary text-gray-700 text-sm">
          {adults}
        </span>

        <BsChevronDown
          className={`text-gray-400 transition-transform duration-300 ${
            isOpen ? "rotate-180 text-blue-600" : ""
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      <div
        className={`absolute left-0 top-full z-40 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden transition-all duration-200 ${
          isOpen
            ? "visible opacity-100 translate-y-0"
            : "invisible opacity-0 -translate-y-2"
        }`}
        role="listbox"
      >
        <ul className="py-1 max-h-60 overflow-y-auto">
          {adultsList.map((item: { name: string }, index: number) => (
            <li key={index}>
              <button
                type="button"
                onClick={() => handleSelect(item.name)}
                className={`w-full px-6 py-2.5 text-left font-secondary text-sm transition-colors duration-150 ${
                  adults === item.name
                    ? "bg-blue-50 text-blue-700 font-semibold"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
                role="option"
                aria-selected={adults === item.name}
              >
                {item.name}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}